// The workability ribbon: one coloured cell per forecast hour, saying whether the
// selected vessel and operation can work that hour.
//
// It used to live in the Site panel beside its own row of day labels, which made
// it the only time series in the console NOT drawn on the shared axis. That is
// exactly backwards: every track in the stack is an INPUT — wind, waves, period,
// light — and this is the OUTPUT. "Why is Thursday no-go?" is answered by looking
// straight down the column from this ribbon to the wind spread and the wave
// height, and that reading only works if the columns line up.
//
// Hence the canvas. The charts map data point i to
//
//     x(i) = left + (i / (n - 1)) * (right - left)
//
// with the first and last points sitting ON the edges of the plot. A row of n
// equal flex spans — what the DOM ribbon was — puts hour i at i/n .. (i+1)/n of
// the width instead, so its centre lands at (i + 0.5)/n. The two mappings agree
// only in the middle of the series and drift apart towards both ends, by Δ/2 at
// the extremes (Δ = one hour's width): with 170 hours in an 800 px plot, hour 0
// is drawn ~2.4 px right of the tick that labels it and the last hour ~2.4 px
// left of it, in opposite directions, which is why the error never looks like a
// simple offset you could nudge out.
//
// So each cell is drawn CENTRED on x(i): x(i) ± Δ/2 with Δ = (right - left) /
// (n - 1), clamped to the plot at both ends. The half-width cells that leaves at
// each end are correct rather than a rounding artefact — hour 0's cell covers
// only the half hour after its sample because there is no earlier sample to fill
// the other half.
//
// Two behaviours here are load-bearing and easy to lose in a rewrite:
//   - a null probability is drawn as NOTHING, never as a colour. "We have no
//     model data for this hour" and "you cannot work this hour" are different
//     answers and painting the first as the second is the dangerous direction.
//   - hours already past are drawn faint, so the eye starts at the now line.

import { getCursor, indexAtTime, subscribeCursor } from "../time/cursor.js";
import { workGrade } from "../ops/workability.js";

export interface WorkRibbonOptions {
  /** Must match the tracks' `gutterLeft`, or the ribbon will not sit above the
   *  hours it grades. */
  gutterLeft: number;
  /** Matches the charts' right margin. */
  gutterRight?: number;
}

export interface WorkRibbonData {
  /** Per-hour probability the limits are met (0..1); null where there is no data. */
  p: readonly (number | null)[];
  /** The forecast's hour labels — shared with every chart in the stack, and the
   *  only way the playhead (a timestamp) can be resolved to a cell. */
  times: readonly string[];
  /** Index of the current hour; everything before it is drawn faint. */
  nowIndex: number;
}

export interface WorkRibbon {
  /** Point the ribbon at a computed workability, or `null` to clear it. */
  update(data: WorkRibbonData | null): void;
  destroy(): void;
}

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v.length > 0 ? v : fallback;
}

export function createWorkRibbon(
  canvas: HTMLCanvasElement,
  opts: WorkRibbonOptions,
): WorkRibbon {
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("ribbon: 2d context unavailable");
  const g = ctx;
  const gutterRight = opts.gutterRight ?? 10;
  let data: WorkRibbonData | null = null;
  let raf = false;

  function draw(): void {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, rect.width, rect.height);

    const left = opts.gutterLeft;
    const right = rect.width - gutterRight;
    const n = data?.p.length ?? 0;
    if (data === null || n === 0 || right <= left) return;

    // The band is inset a little from the track's full height: a ribbon that
    // touched the row's edges would read as a background wash rather than as a
    // series drawn on the same axis as the traces above and below it.
    const top = 6;
    const bot = Math.max(top + 8, rect.height - 8);
    const denom = n > 1 ? n - 1 : 1;
    const step = (right - left) / denom;
    const xPx = (i: number): number => left + step * i;

    for (let i = 0; i < n; i++) {
      const p = data.p[i];
      if (p == null || !Number.isFinite(p)) continue;
      const grade = workGrade(p);
      // Round the edges so adjacent cells share a pixel boundary instead of
      // leaving hairline seams that read as missing hours.
      const a = Math.round(Math.max(left, xPx(i) - step / 2));
      const b = Math.round(Math.min(right, xPx(i) + step / 2));
      g.globalAlpha = i < data.nowIndex ? 0.3 : 1;
      g.fillStyle = cssVar(grade.varName, grade.fallback);
      g.fillRect(a, top, Math.max(1, b - a), bot - top);
    }
    g.globalAlpha = 1;

    // A hairline round the band. Without it a run of no-data hours at the end of
    // the series looks like the ribbon simply stops early rather than like a gap
    // in the models.
    g.strokeStyle = cssVar("--line", "#d7dee5");
    g.lineWidth = 1;
    g.strokeRect(left + 0.5, top + 0.5, right - left - 1, bot - top - 1);

    // The playhead, drawn the way the charts draw it, so the eye tracks one line
    // down the whole stack.
    const idx = indexAtTime(data.times, getCursor());
    if (idx === null) return;
    const x = Math.round(xPx(idx)) + 0.5;
    // --accent, matching ChartCtx's cursor: deliberately not --crit, so the
    // timeline position is never mistaken for an alarm on a red no-go cell.
    g.strokeStyle = cssVar("--accent", "#0f6b74");
    g.beginPath();
    g.moveTo(x, top);
    g.lineTo(x, bot);
    g.stroke();
  }

  function requestDraw(): void {
    if (raf) return;
    raf = true;
    requestAnimationFrame(() => {
      raf = false;
      draw();
    });
  }

  const unsubscribe = subscribeCursor(requestDraw);
  const ro = new ResizeObserver(requestDraw);
  ro.observe(canvas.parentElement ?? canvas);
  draw();

  return {
    update(next): void {
      data = next;
      draw();
    },
    destroy(): void {
      unsubscribe();
      ro.disconnect();
    },
  };
}
